import { index, stratify, InternMap } from "d3";
import { HierarchyNode } from "d3-hierarchy";
import { ProductLevel } from "../types/enums";
import type {
  ProductMetadatum,
  ProductMetadataByLevel,
  LocationMetadatum,
  YearIndexable,
  StratifyProductInput,
} from "../types/metadata";
import { locationMembersInclude } from "../types/metadata";

export function createProductLookupIndex(
  products: ProductMetadatum[],
): InternMap<string, ProductMetadatum> {
  return index(
    products,
    (d: ProductMetadatum) => d.productId,
  ) as InternMap<string, ProductMetadatum>;
}

export function createYearLookupIndex<T extends YearIndexable>(
  yearData: T[] | undefined | null,
): InternMap<number, T> {
  return index(
    yearData ?? [],
    (d: T) => d.year ?? -1,
  ) as InternMap<number, T>;
}

export function enrichLocationWithHierarchy(
  countryId: number | string,
  regions: LocationMetadatum[],
  subregions: LocationMetadatum[],
  countries: LocationMetadatum[],
): {
  topLevelParent: string | undefined;
  hybrid_level_1: { id: string; nameEn: string };
  hybrid_level_2: { id: string; nameEn: string };
  nameEn: string;
  countryIso3Code?: string;
} {
  const regionMetadata = regions.find((region) =>
    locationMembersInclude(region.members, countryId),
  );
  const topLevelParent = regionMetadata?.groupId || "undisclosed";
  const hybrid_level_1 = {
    id: topLevelParent,
    nameEn: regionMetadata?.groupName || "undisclosed",
  };

  const subregionMetadata = subregions.find((subregion) =>
    locationMembersInclude(subregion.members, countryId),
  );
  const hybrid_level_2 = {
    id: subregionMetadata?.groupId || "undisclosed",
    nameEn: subregionMetadata?.groupName || "undisclosed",
  };

  const countryMetadata = countries.find(
    (country) =>
      country.countryId != null &&
      String(country.countryId) === String(countryId),
  );
  const nameEn = countryMetadata?.nameShortEn || "undisclosed";
  const countryIso3Code = countryMetadata?.iso3Code ?? undefined;

  return {
    topLevelParent,
    hybrid_level_1,
    hybrid_level_2,
    nameEn,
    countryIso3Code,
  };
}

/** Return type for createLocationHierarchyIndex */
export interface LocationHierarchyIndex {
  regionByMember: Map<string, LocationMetadatum>;
  subregionByMember: Map<string, LocationMetadatum>;
  countryById: Map<string, LocationMetadatum>;
}

/**
 * Pre-compute member->region and member->subregion Maps for O(1) lookups.
 *
 * GraphQL ID scalars are strings at runtime (e.g. "42" or "country-42").
 * We use String() keys so lookups match regardless of the ID format.
 */
interface LocationLike {
  groupId?: string;
  groupName?: string;
  members?: (number | string | null)[];
  countryId?: number | string;
  nameShortEn?: string | null;
  iso3Code?: string | null;
}
export function createLocationHierarchyIndex(
  regions: LocationLike[],
  subregions: LocationLike[],
  countries: LocationLike[],
): LocationHierarchyIndex {
  const regionByMember = new Map<string, LocationMetadatum>();
  regions.forEach((r) =>
    r.members?.forEach((m) => regionByMember.set(String(m), r as LocationMetadatum)),
  );

  const subregionByMember = new Map<string, LocationMetadatum>();
  subregions.forEach((s) =>
    s.members?.forEach((m) => subregionByMember.set(String(m), s as LocationMetadatum)),
  );

  const countryById = new Map<string, LocationMetadatum>();
  countries.forEach((c) => {
    if (c.countryId != null) countryById.set(String(c.countryId), c as LocationMetadatum);
  });

  return { regionByMember, subregionByMember, countryById };
}

/** Fast version of enrichLocationWithHierarchy using pre-computed index */
export function enrichLocationWithHierarchyFast(
  countryId: number | string,
  idx: LocationHierarchyIndex,
): {
  topLevelParent: string | undefined;
  hybrid_level_1: { id: string; nameEn: string };
  hybrid_level_2: { id: string; nameEn: string };
  nameEn: string;
  countryIso3Code?: string;
} {
  // GraphQL ID scalars are strings at runtime; Map keys are stored as strings.
  const key = String(countryId);

  const regionMetadata = idx.regionByMember.get(key);
  const topLevelParent = regionMetadata?.groupId || "undisclosed";
  const hybrid_level_1 = {
    id: topLevelParent,
    nameEn: regionMetadata?.groupName || "undisclosed",
  };

  const subregionMetadata = idx.subregionByMember.get(key);
  const hybrid_level_2 = {
    id: subregionMetadata?.groupId || "undisclosed",
    nameEn: subregionMetadata?.groupName || "undisclosed",
  };

  const countryMetadata = idx.countryById.get(key);
  const nameEn = countryMetadata?.nameShortEn || "undisclosed";
  const countryIso3Code = countryMetadata?.iso3Code ?? undefined;

  return {
    topLevelParent,
    hybrid_level_1,
    hybrid_level_2,
    nameEn,
    countryIso3Code,
  };
}

export function createProductHierarchyTree(
  metadata: ProductMetadatum[],
): HierarchyNode<StratifyProductInput> {
  if (!metadata || metadata.length === 0) {
    return stratify<StratifyProductInput>()
      .id((d) => ("productId" in d ? d.productId : "root"))
      .parentId(() => null)([{ productId: "root", parent: null }]);
  }

  const inputData: StratifyProductInput[] = [
    { productId: "root", parent: null },
    ...metadata,
  ];

  const tree = stratify<StratifyProductInput>()
    .id((d: StratifyProductInput) => d.productId)
    .parentId((d: StratifyProductInput) => {
      if (!("productLevel" in d)) {
        // This is the root node
        return null;
      }
      if (d.productLevel === ProductLevel.ProductSection) {
        return "root";
      } else if (d.parent) {
        return d.parent.productId;
      } else if (d.topParent) {
        return d.topParent.productId;
      }
      return "root";
    })(inputData);

  return tree;
}

/**
 * Flatten a ProductMetadataByLevel into a single array of all products.
 */
export function flattenProductMetadata(
  data: ProductMetadataByLevel | undefined,
): ProductMetadatum[] {
  if (!data) return [];
  return [
    ...(data.section || []),
    ...(data.twoDigit || []),
    ...(data.fourDigit || []),
    ...(data.sixDigit || []),
  ];
}
